<template>
	<div class="join-form">
		<div class="top-content">
			<ProfilePic class="org-logo" :user="invite.organization" size="100px" />

			<div class="org-name" v-text="invite.organization.name" />
			<div class="join-group">
				{{
					$t('groupinvites_signup_join_group_label', {
						GroupName: invite.group.title,
					})
				}}
			</div>
		</div>

		<FormItem
			:label="$t('onboarding_name_input_firstname_placeholder')"
			:error="!!errors.firstname"
			:message="errors.firstname"
		>
			<Input v-model="firstname" />
		</FormItem>

		<FormItem
			:label="$t('onboarding_name_input_lastname_placeholder')"
			:error="!!errors.lastname"
			:message="errors.lastname"
		>
			<Input v-model="lastname" />
		</FormItem>

		<FormItem
			:label="$t(`onboarding_loginmethod_${contactField}_button`)"
			:error="!!errors[contactField]"
			:message="errors[contactField]"
		>
			<Input
				v-if="contactField === 'email'"
				v-model="contact"
				@enter="submit"
			/>

			<PhoneInput
				v-if="contactField === 'phone'"
				v-model="contact"
				:error="!!errors[contactField]"
				@keyup.enter="submit"
				large
			/>

			<template #alert-icon>
				<div @click="toggleField" class="verification-method">
					{{ $t(`onboarding_phone_switchmode_${contactField}`) }}
				</div>
			</template>
		</FormItem>

		<Button
			pronto
			block
			large
			class="join-pronto-button"
			:disabled="disabled"
			:loading="processing"
			@click="submit"
		>
			{{ $t('groupinvites_signup_join_action') }}
		</Button>

		<div
			class="bottom-content"
			v-html="
				$t('onboarding_phone_termsofservice', {
					TermsUrl: 'https://www.pronto.io/user-terms',
					PrivacyUrl: 'https://www.pronto.io/privacy',
				})
			"
		/>

		<StandardModal
			v-if="!!duplicateDeviceError"
			open
			small
			:title="`Duplicate ${duplicateDeviceError}`"
			@cancel="duplicateDeviceError = null"
			class="duplicate-device-modal"
		>
			<div class="duplicate-device-content">
				<CircleExclamationIcon type="regular" halo />

				<div class="help">
					{{
						$t(`apierrors_${duplicateDeviceError.toUpperCase()}_ALREADY_USED`)
					}}
				</div>
			</div>
			<template #footer-right>
				<Button primary :to="signin">
					{{ $t('onboarding_splash_signin') }}
				</Button>
			</template>
		</StandardModal>
	</div>
</template>

<script>
import {
	FormItem,
	Input,
	Button,
	ProfilePic,
	PhoneInput,
	StandardModal,
} from '@/components/common'
import {
	getInvite,
	setUserData,
	sendCodeToNewUser,
	verificationRoute,
	signinRoute,
} from './index'
import { CircleExclamationIcon } from '@/icons'

export default {
	components: {
		FormItem,
		Input,
		Button,
		ProfilePic,
		PhoneInput,
		StandardModal,
		CircleExclamationIcon,
	},

	data() {
		return {
			duplicateDeviceError: null,
			errors: {},
			invite: null,
			firstname: null,
			lastname: null,
			contact: null,
			contactField: 'phone',
			processing: false,
		}
	},

	created() {
		this.invite = getInvite()
	},

	computed: {
		signin() {
			return { name: signinRoute.name, params: this.$route.params }
		},

		disabled() {
			return !this.firstname || !this.lastname || !this.contact
		},
	},

	methods: {
		displayErrors(e) {
			if (!e.response?.data?.errors) {
				this.error = this.$alert.error()
				return
			}

			const errors = Object.entries(e.response?.data?.errors).reduce(
				(acc, [key, errors]) => {
					errors[0].code === 'DUPLICATE'
						? (acc[`${key}_code`] = errors[0].code)
						: (acc[key] = errors[0])
					return acc
				},
				{}
			)

			if (errors.phone_code === 'DUPLICATE') {
				this.duplicateDeviceError = 'phone'
			}

			if (errors.email_code === 'DUPLICATE') {
				this.duplicateDeviceError = 'email'
			}

			this.errors = errors
		},

		async submit() {
			if (this.disabled) return

			this.processing = true

			const payload = {
				firstname: this.firstname,
				lastname: this.lastname,
				[this.contactField]: this.contact,
			}

			try {
				await sendCodeToNewUser(payload)
				this.$alert.success(this.$t('common_confirmation_sent_nocheck'))
				setUserData(payload)

				this.$router.push({
					name: verificationRoute.name,
				})
			} catch (e) {
				this.displayErrors(e)
			}

			this.processing = false
		},

		toggleField() {
			this.contactField = this.contactField === 'phone' ? 'email' : 'phone'
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.join-form {
	.top-content {
		display: flex;
		align-items: center;
		flex-direction: column;

		.org-logo {
			margin-bottom: 6px;
		}

		.org-name {
			font-family: Avenir-Book;
			font-size: 28px;
			color: var(--neutral-800);
			margin-bottom: 16px;

			@media screen and (max-width: 580px) {
				font-size: 20px;
			}
		}

		.join-group {
			font-family: Avenir-Book;
			font-size: 14px;
			color: $neutral-500;
			margin-bottom: 35px;

			@media screen and (max-width: 580px) {
				margin-bottom: 28px;
			}
		}
	}

	.verification-method {
		@include fontFamily(800);
		color: var(--teal-300);
		cursor: pointer;
		font-size: 14px;
	}

	.form-item {
		margin-bottom: 24px;

		@media screen and (max-width: 580px) {
			margin-bottom: 20px;
		}

		:deep(.form-item-header) {
			.form-item-label {
				@media screen and (max-width: 580px) {
					font-size: 14px;
				}
			}
		}

		.pronto-input {
			height: 60px;
		}
	}

	.bottom-content {
		margin-top: 20px;
		font-family: Avenir-Book;
		color: $neutral-500;
		font-size: 14px;
		text-align: center;

		:deep(a) {
			color: var(--teal-300);
			text-decoration: none;
		}
	}

	.duplicate-device-modal {
		:deep(.modal-title) {
			text-transform: capitalize;
		}
	}
}

.duplicate-device-content {
	padding: 24px 60px;
	@include flex-column-center;
	text-align: center;
	.pronto-icon {
		color: var(--red-200);
		font-size: 45px;
		margin-bottom: 25px;
	}
}
</style>
